'use client'

import { consentedTo, ConsentGroup } from '@repo/cookie/consent'
import { imageQuality } from '@repo/sanity/image'
import { SingleProductQueryResult } from '@repo/sanity/types'
import { Product } from '@repo/shopify/types'
import { LOCAL_STORAGE_EMAIL_UNVERIFIED, LOCAL_STORAGE_OUT_OF_STOCK_SIGNUP_DIALOG, setInStorage } from '@repo/storage/service'
import { Button } from '@repo/ui/components/button'
import { Checkbox } from '@repo/ui/components/checkbox'
import { Dialog, DialogAside, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@repo/ui/components/dialog'
import { Input } from '@repo/ui/components/input'
import { cn } from '@repo/ui/lib/utils'
import { extractLastPart } from '@repo/utils/text'
import { Image } from 'next-sanity/image'
import { useSearchParams } from 'next/navigation'
import { FunctionComponent, useActionState, useCallback, useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { outOfStockSignup as outOfStockSignupAction } from '../../../actions/outOfStockSignup.actions'
import { DIALOG, useDialog } from '../../../providers/DialogProvider'
import { useProduct } from '../../../providers/ProductProvider'
import { PageDto } from '../../../types/page.types'
import { ProductDto } from '../../../types/product.types'

interface Props {
  pageDto: PageDto
  productDto: ProductDto
  shopifyProduct: Product
  outOfStockSignupSettings?: NonNullable<SingleProductQueryResult>['outOfStockSignupSettings']
}

const ProductOutOfStockSignupDialog: FunctionComponent<Props> = ({ pageDto, productDto, shopifyProduct, outOfStockSignupSettings }) => {
  const { getSelectedVariant } = useProduct()
  const searchParams = useSearchParams()
  const { isDialogOpen, closeDialog, openDialog } = useDialog()
  const selectedVariantIdRef = useRef<string | null>(null)
  const toastShownRef = useRef<{ [key: string]: boolean }>({})
  const [state, formAction, pending] = useActionState(outOfStockSignupAction, null)
  const image = productDto?.sanityProduct?.images?.[0]
  const selectedVariant = getSelectedVariant(shopifyProduct)
  const selectedVariantId = extractLastPart(selectedVariant?.id || '')

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setInStorage(LOCAL_STORAGE_OUT_OF_STOCK_SIGNUP_DIALOG, Date.now())

      if (!open) {
        closeDialog(DIALOG.OUT_OF_STOCK_SIGNUP)

        // Clean up URL, i.e. remove size=X from URL params.
        const urlSearchParams = new URLSearchParams(searchParams.toString())

        for (const selectedOption of selectedVariant?.selectedOptions || []) {
          urlSearchParams.delete(selectedOption.name.toLowerCase())
        }

        window.history.replaceState(null, '', `?${urlSearchParams.toString()}`)
      }
    },
    [closeDialog, searchParams, selectedVariant?.selectedOptions],
  )

  const handleSubmit = () => {
    toastShownRef.current = {}
    selectedVariantIdRef.current = null
  }

  // On selecting variant.
  useEffect(() => {
    if (selectedVariant) {
      if (selectedVariant.availableForSale) {
        if (isDialogOpen(DIALOG.OUT_OF_STOCK_SIGNUP)) {
          closeDialog(DIALOG.OUT_OF_STOCK_SIGNUP)
        }
      } else {
        if (!isDialogOpen(DIALOG.OUT_OF_STOCK_SIGNUP)) {
          openDialog(DIALOG.OUT_OF_STOCK_SIGNUP)
        }
      }
    } else {
      closeDialog(DIALOG.OUT_OF_STOCK_SIGNUP)
    }

    // Not sure why, but adding in all dependencies break the dialog
    // close feature in the sense that it automatically re-opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariant])

  // On success.
  useEffect(() => {
    if (pending || !state?.status) {
      return
    }

    if (!toastShownRef.current[state.status]) {
      if (state.status === 'success') {
        toast.success(outOfStockSignupSettings?.dialogSuccessMessage || 'Thank you for signing up! You will receive an email as soon as this product is back in stock.')
      } else if (state.status === 'error') {
        toast.error(state?.message || 'Something went wrong.')
      }

      // Mark the toast as shown for this status, as
      // to prevent any duplicate toasts due to re-renders.
      toastShownRef.current[state?.status] = true
    }

    if (state.status === 'success' && selectedVariantIdRef.current === null) {
      handleOpenChange(false)

      if (state?.validatedFields?.data?.email && consentedTo(ConsentGroup.PREFERENCES)) {
        setInStorage(LOCAL_STORAGE_EMAIL_UNVERIFIED, state.validatedFields.data.email)
      }

      // Ensure we close just once, otherwise we call handleOpenChange(false) too often
      // (upon every re-render), preventing additional openings of the dialog.
      selectedVariantIdRef.current = selectedVariantId
    }
  }, [handleOpenChange, outOfStockSignupSettings?.dialogSuccessMessage, pending, selectedVariantId, state])

  if (!shopifyProduct || !outOfStockSignupSettings?.dialogTitle || !outOfStockSignupSettings?.dialogCopy) {
    return null
  }

  const FieldError: FunctionComponent<{ field: string }> = ({ field }) => {
    return <p className={cn('text-destructive pt-2 empty:hidden', pending && 'hidden')}>{state?.status === 'error' ? (state?.errors as any)?.[field]?.[0] : null}</p>
  }

  return (
    <Dialog open={isDialogOpen(DIALOG.OUT_OF_STOCK_SIGNUP)} onOpenChange={handleOpenChange} modal={false}>
      <DialogContent className='flex-col gap-3 sm:max-w-[22rem]' position='bottomRight' background='white'>
        <div className='flex items-stretch justify-between gap-3'>
          {image?.asset?.url && (
            <DialogAside className='relative aspect-[2/3]'>
              <Image
                className='rounded-lg object-cover'
                fill={true}
                priority={false}
                quality={imageQuality}
                src={image?.asset?.url}
                alt={image.alt || String(productDto?.sanityProduct?.title || '')}
                blurDataURL={image?.asset?.metadata?.lqip ?? undefined}
                placeholder={image?.asset?.metadata?.lqip ? 'blur' : undefined}
                sizes='7rem'
              />
            </DialogAside>
          )}
          <div className='flex flex-col justify-between'>
            <DialogHeader>
              <DialogTitle>{outOfStockSignupSettings?.dialogTitle}</DialogTitle>
            </DialogHeader>
            <DialogDescription className='max-w-[10rem] text-pretty'>
              {outOfStockSignupSettings?.dialogCopy || 'Would you like to receive an email when this style becomes available?'}
            </DialogDescription>
          </div>
        </div>
        <form className='flex flex-1 basis-full flex-col gap-3 pb-2' action={formAction} onSubmit={handleSubmit}>
          <input type='hidden' name='locale' value={pageDto.params.locale} />
          <input type='hidden' name='selectedVariantId' value={selectedVariantId} />
          <div>
            <Input
              type='email'
              name='email'
              variant='underlined'
              placeholder={pageDto.dictionary?.outofstock?.enteryouremail || 'Enter your email'}
              autoComplete='email'
              required={true}
            />
            <FieldError field='email' />
          </div>
          {outOfStockSignupSettings?.dialogKlaviyoNewsletterSubscribeList && selectedVariantId && (
            <div className='flex items-center space-x-2'>
              <Checkbox id='subscribe' name='subscribe' />
              <label htmlFor='subscribe'>{pageDto.dictionary?.outofstock?.subscribetothenewsletter || 'Subscribe to the newsletter'}</label>
              <input type='hidden' name='klaviyoList' value={outOfStockSignupSettings.dialogKlaviyoNewsletterSubscribeList} />
            </div>
          )}
          <DialogFooter>
            <Button type='submit' disabled={pending} variant={state?.status === 'error' && !pending ? 'destructive' : 'defaultStatic'}>
              {outOfStockSignupSettings?.dialogButtonLabel || 'Sign Up'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default ProductOutOfStockSignupDialog
